import { bodyPreviewText } from './richText';

const GENERIC_AUTHOR_LABELS = new Set(['unknown', 'ai agent', 'ai worker', 'someone']);

/**
 * @param {string} label
 * @return {boolean}
 */
function isGenericAuthorLabel(label) {
  if (typeof label !== 'string' || !label.trim()) return true;
  return GENERIC_AUTHOR_LABELS.has(label.trim().toLowerCase());
}

/**
 * @param {string} agentId
 * @return {boolean}
 */
function looksLikeFirestoreAutoId(agentId) {
  return typeof agentId === 'string' && /^[A-Za-z0-9]{20}$/.test(agentId);
}

/**
 * @param {Record<string, object>} agentById
 * @param {string} ref
 * @return {object|null}
 */
function findAgentInMap(agentById, ref) {
  const key = typeof ref === 'string' ? ref.trim() : '';
  if (!key || !agentById || typeof agentById !== 'object') return null;
  if (agentById[key]) return agentById[key];
  const lower = key.toLowerCase();
  return (
    Object.values(agentById).find((agent) => {
      const name = typeof agent?.name === 'string' ? agent.name.trim() : '';
      const id = typeof agent?.id === 'string' ? agent.id.trim() : '';
      return name.toLowerCase() === lower || id.toLowerCase() === lower;
    }) || null
  );
}

/**
 * @param {object|null|undefined} agent
 * @param {string} agentId
 * @param {string} [fallbackName]
 * @param {Record<string, object>} [agentById]
 * @return {string}
 */
export function aiAgentDisplayName(agent, agentId, fallbackName = '', agentById = {}) {
  if (agent?.name) return agent.name;
  if (typeof fallbackName === 'string' && fallbackName.trim()) return fallbackName.trim();
  if (agent?.roleTitle) return agent.roleTitle;

  const id = typeof agentId === 'string' ? agentId.trim() : '';
  if (id) {
    const byRef = findAgentInMap(agentById, id);
    if (byRef?.name) return byRef.name;
    if (!looksLikeFirestoreAutoId(id)) return id;
    if (byRef?.roleTitle) return byRef.roleTitle;
  }

  if (id) return id;
  return 'AI agent';
}

/**
 * @param {string|null|undefined} authorUid
 * @param {Record<string, object>} [agentById]
 * @param {string} [fallbackName]
 * @return {string|null}
 */
export function commentAuthorFromUid(authorUid, agentById = {}, fallbackName = '') {
  const uid = typeof authorUid === 'string' ? authorUid.trim() : '';
  if (!uid) return null;
  if (uid.startsWith('ai_agent:')) {
    const agentId = uid.slice('ai_agent:'.length);
    return aiAgentDisplayName(
      agentById[agentId] || findAgentInMap(agentById, agentId),
      agentId,
      fallbackName,
      agentById,
    );
  }
  if (uid.startsWith('system:')) return 'System';
  return null;
}

/**
 * @param {object|null|undefined} ticket
 * @return {boolean}
 */
export function ticketHasAiAssignment(ticket) {
  if (!ticket || typeof ticket !== 'object') return false;
  return Boolean(
    ticket.isAssignedToAi && ticket.assigneeAgentId,
  );
}

/**
 * @param {object|null|undefined} ticket
 * @param {Record<string, object>} [agentById]
 * @return {string|null}
 */
export function ticketDelegatorAgentId(ticket) {
  if (!ticket || typeof ticket !== 'object') return null;
  if (typeof ticket.delegatedByAgentId === 'string' && ticket.delegatedByAgentId.trim()) {
    return ticket.delegatedByAgentId.trim();
  }
  const createdBy = typeof ticket.createdBy === 'string' ? ticket.createdBy.trim() : '';
  if (createdBy.startsWith('ai_agent:')) {
    return createdBy.slice('ai_agent:'.length);
  }
  return null;
}

/**
 * @param {object|null|undefined} comment
 * @param {Record<string, object>} [agentById]
 * @return {string}
 */
export function commentAuthorLabel(comment, agentById = {}) {
  if (!comment || typeof comment !== 'object') return 'Unknown';

  const agentId =
    (typeof comment.agentId === 'string' && comment.agentId.trim()) ||
    (typeof comment.authorUid === 'string' && comment.authorUid.startsWith('ai_agent:')
      ? comment.authorUid.slice('ai_agent:'.length)
      : '');
  const agent = agentId ? findAgentInMap(agentById, agentId) : null;
  const isAi =
    comment.isAiResponse === true ||
    comment.isAi === true ||
    comment.senderType === 'agent' ||
    Boolean(agentId);

  if (isAi) {
    const fromAgent = aiAgentDisplayName(
      agent,
      agentId,
      comment.authorDisplayName,
      agentById,
    );
    if (!isGenericAuthorLabel(fromAgent)) return fromAgent;
  }

  if (comment.authorDisplayName && !isGenericAuthorLabel(comment.authorDisplayName)) {
    return comment.authorDisplayName;
  }
  if (comment.authorEmail) return comment.authorEmail;

  const fromUid = commentAuthorFromUid(
    comment.authorUid,
    agentById,
    comment.authorDisplayName,
  );
  if (fromUid) return fromUid;

  if (agentId) {
    return aiAgentDisplayName(agent, agentId, '', agentById);
  }
  if (isAi) return 'AI agent';
  if (comment.authorUid) return comment.authorUid;
  return 'Unknown';
}

/**
 * @param {object|null|undefined} t
 * @param {Record<string, object>} userByUid
 * @param {Record<string, object>} [agentById]
 * @return {string}
 */
export function ticketCreatorLabel(t, userByUid, agentById = {}) {
  if (!t || typeof t !== 'object') return 'Unknown';

  const createdBy = typeof t.createdBy === 'string' ? t.createdBy.trim() : '';
  const isAiCreated =
    t.source === 'ai_agent_delegation' ||
    Boolean(t.delegatedByAgentId) ||
    ticketHasAiAssignment(t) ||
    createdBy.startsWith('ai_agent:') ||
    createdBy.startsWith('system:');

  if (isAiCreated) {
    const delegatorId = ticketDelegatorAgentId(t);
    if (delegatorId) {
      const delegator = findAgentInMap(agentById, delegatorId);
      const delegatorName = aiAgentDisplayName(
        delegator,
        delegatorId,
        t.delegatedByAgentName || '',
        agentById,
      );
      return `${delegatorName} (AI agent)`;
    }
    if (t.assigneeAgentName) {
      return `${t.assigneeAgentName} (AI worker)`;
    }
    if (t.assigneeAgentId) {
      const assignee = findAgentInMap(agentById, t.assigneeAgentId);
      return `${aiAgentDisplayName(assignee, t.assigneeAgentId, '', agentById)} (AI worker)`;
    }
    return 'AI agent';
  }

  if (createdBy.startsWith('ai_agent:')) {
    const agentId = createdBy.slice('ai_agent:'.length);
    const agent = findAgentInMap(agentById, agentId);
    return `${aiAgentDisplayName(agent, agentId, t.delegatedByAgentName || '', agentById)} (AI agent)`;
  }
  if (createdBy.startsWith('system:')) return 'System';

  const creatorUid = createdBy || t.reporterUid;
  if (t.createdByEmail) return t.createdByEmail;
  if (t.reporterEmail) return t.reporterEmail;
  if (t.createdByName) return t.createdByName;
  const human = userByUid[creatorUid];
  if (human) return human.displayName || human.email || human.uid;
  if (typeof creatorUid === 'string' && creatorUid) return creatorUid;
  return 'Unknown';
}

/**
 * @param {object|null|undefined} t
 * @return {string}
 */
export function ticketListPreviewText(t) {
  if (!t) return '';
  if (typeof t.lastCommentPreview === 'string' && t.lastCommentPreview.trim()) {
    return t.lastCommentPreview.trim();
  }
  return bodyPreviewText(t.body, t.bodyFormat, 80) || (typeof t.body === 'string' ? t.body : '');
}

/**
 * @param {object|null|undefined} t
 * @param {Record<string, object>} userByUid
 * @param {Record<string, object>} agentById
 * @param {(value: unknown) => string} formatDateTime
 * @return {string}
 */
export function ticketListActivityMeta(t, userByUid, agentById, formatDateTime) {
  if (typeof t?.lastCommentPreview === 'string' && t.lastCommentPreview.trim()) {
    const storedAuthor =
      typeof t.lastCommentAuthor === 'string' ? t.lastCommentAuthor.trim() : '';
    const who = !isGenericAuthorLabel(storedAuthor)
      ? storedAuthor
      : commentAuthorFromUid(t.lastCommentAuthorUid, agentById, storedAuthor) ||
        (ticketHasAiAssignment(t) ? t.assigneeAgentName : null) ||
        ticketCreatorLabel(t, userByUid, agentById) ||
        'Someone';
    const when = formatDateTime(t.lastCommentAt);
    return `Latest from ${who}${when !== '—' ? ` · ${when}` : ''}`;
  }
  const creator = ticketCreatorLabel(t, userByUid, agentById);
  const when = formatDateTime(t.createdAt);
  return `Created by ${creator}${when !== '—' ? ` · ${when}` : ''}`;
}

/**
 * @param {object} comment
 * @param {Record<string, object>} [agentById]
 * @param {number} [maxLen]
 * @return {object}
 */
export function buildLastCommentTicketPatch(comment, agentById = {}, maxLen = 120) {
  const body = typeof comment.body === 'string' ? comment.body : '';
  const bodyFormat = comment.bodyFormat || 'plain';
  return {
    lastCommentPreview: bodyPreviewText(body, bodyFormat, maxLen),
    lastCommentAuthor: commentAuthorLabel(comment, agentById),
    lastCommentAuthorUid: comment.authorUid || null,
    lastCommentAt: comment.createdAt || null,
  };
}
